import { useState } from "react";
import { STATUS_ENDPOINT } from "../constants";

// One server round-trip for single or bulk status changes, then an optimistic
// local update so the UI reflects it without a refetch. Tracks which contracts
// are in flight (`busy`) and which failed (`errored`).
export function useStatusUpdate({ api, setContracts }) {
  const [busy, setBusy] = useState(() => new Set());
  const [errored, setErrored] = useState(() => new Set());

  const clearErrors = () => setErrored(new Set());

  const updateStatus = async (numbers, status) => {
    if (!numbers.length) return;
    setBusy((prev) => new Set([...prev, ...numbers]));
    // Clear prior failures for these — this click is a (re)try.
    setErrored((prev) => {
      const next = new Set(prev);
      numbers.forEach((n) => next.delete(n));
      return next;
    });
    try {
      const response = await api.post(STATUS_ENDPOINT[status], {
        contractNumbers: numbers,
      });
      // The API echoes back the contracts it actually updated. A requested
      // number present in that list succeeded; one that's missing failed.
      // (Adjust `r.number` if the result items aren't shaped like contracts.)
      const results = response?.data?.contractNumbers ?? [];
      const returned = new Set(results.map((r) => r));
      const succeeded = numbers.filter((n) => returned.has(n));
      const failed = numbers.filter((n) => !returned.has(n));

      if (succeeded.length) {
        const set = new Set(succeeded);
        setContracts((prev) =>
          prev.map((c) =>
            set.has(c.number) ? { ...c, commissionStatus: status } : c,
          ),
        );
      }
      if (failed.length) {
        setErrored((prev) => new Set([...prev, ...failed]));
      }
    } catch {
      // Network/server error: treat the whole batch as failed.
      setErrored((prev) => new Set([...prev, ...numbers]));
    } finally {
      setBusy((prev) => {
        const next = new Set(prev);
        numbers.forEach((n) => next.delete(n));
        return next;
      });
    }
  };

  return { updateStatus, busy, errored, clearErrors };
}
